import { action, makeObservable, observable, ObservableMap } from "mobx";

export enum ChatActionType {
    SelectHierarchy = "SelectHierarchy",
    SelectTheme = "SelectTheme",
    SelectClassifier = "SelectClassifier",
    SelectModule = "SelectModule",
    SelectAgent = "SelectAgent",
    SelectContactType = "SelectContactType",
    SelectAllModulesInCategory = "SelectAllModulesInCategory",
    AddPreExistingGraphToDashboard = "AddPreExistingGraphToDashboard",
    SelectModelResult = "SelectModelResult",
    SelectModel = "SelectModel",
    ViewSqlQueries = "ViewSqlQueries",
    ViewDebug = "ViewDebug",
}

/**
 * Represents the map/dict that we receive from BE when actions are present.
 * The actual value for each key may vary hence the unknown.
 */
export type ChatActionsMap = {
    [ChatActionType.SelectHierarchy]: unknown;
    [ChatActionType.SelectTheme]: unknown;
    [ChatActionType.SelectClassifier]: unknown;
    [ChatActionType.SelectModule]: unknown;
    [ChatActionType.SelectAgent]: unknown;
    [ChatActionType.SelectAllModulesInCategory]: unknown;
    [ChatActionType.ViewSqlQueries]: unknown;
    [ChatActionType.ViewDebug]: unknown;
    [ChatActionType.SelectContactType]: unknown;
    [ChatActionType.AddPreExistingGraphToDashboard]: unknown;
    [ChatActionType.SelectModelResult]: unknown;
    [ChatActionType.SelectModel]: unknown;
};

export class ChatActions {
    @observable
    private map: ObservableMap<string, unknown>;

    constructor() {
        makeObservable(this);
        this.map = observable.map();
    }

    public static from(map: ChatActionsMap): ChatActions {
        const actions = new ChatActions();
        for (const [key, value] of Object.entries(map))
            actions.set(key as keyof ChatActionsMap, value);
        return actions;
    }

    public set<T extends keyof ChatActionsMap>(
        type: T,
        value: ChatActionsMap[T],
    ) {
        this.map.set(type, value);
    }

    public get<T extends keyof ChatActionsMap>(type: T): ChatActionsMap[T] {
        return this.map.get(type) as ChatActionsMap[T];
    }

    public has<T extends keyof ChatActionsMap>(type: T) {
        return this.map.has(type);
    }

    @action
    public delete<T extends keyof ChatActionsMap>(type: T) {
        this.map.delete(type);
    }

    public entries() {
        return Array.from(this.map.entries()) as Array<
            [keyof ChatActionsMap, ChatActionsMap[keyof ChatActionsMap]]
        >;
    }
}
